<script lang="ts">
import { defineComponent } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
import { DataModules } from "@/store/modules/data/modules";
import { RegexMatch } from "@/data/enums/router";
import { TrialEndActionTypes } from "@/data/enums/products";
import { UPM_DATA_LAYER } from "@/boot/gtm";
import type { PropType, CreateElement } from "vue";
import type {
  IShopProductProviderData,
  IShopProductProviderMethods
} from "@/models/providers/products";
import type { IState } from "@/store";
import type { IProduct } from "@/models/products";
import type { IBasket } from "@/models/baskets";
import type { ICurrency } from "@/models/constants";
import type { IAccount } from "@/models/accounts";

export default defineComponent({
  name: "ShopProductProvider",
  mixins: [RequestPromises, Context],
  provide() {
    return {
      shopProductProviderData: this
        .shopProductProviderData as IShopProductProviderData,
      shopProductProviderMethods: this
        .shopProductProviderMethods as IShopProductProviderMethods
    };
  },
  props: {
    handleLoading: { type: Boolean, default: true },
    handleRequestError: { type: Boolean, default: true },
    loaderAttrs: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({})
    },
    productId: { type: String as PropType<IProduct["id"]>, required: true },
    accountId: { type: String as PropType<IAccount["id"]>, default: "" },
    basketId: { type: String as PropType<IBasket["id"]>, default: "" },
    currencyId: { type: String as PropType<ICurrency["id"]>, required: true },
    omitPromotions: { type: Boolean, required: false },
    coupons: { type: Array as PropType<string[]>, default: () => [] },
    basketProduct: { type: Object as PropType<any>, default: null }
  },
  data: () => ({
    isLoading: true,
    isReloading: false,
    hasRequestError: false
  }),
  computed: {
    ...mapState({
      productScope(state: IState, getters): string {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      },
      product(state: IState, getters): IProduct {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      }
    }),
    isProductTrial() {
      return this.product.trial_supported;
    },
    hideProductPrices() {
      return (
        this.isProductTrial &&
        [TrialEndActionTypes.CANCEL].includes(this.product.trial_end_action)
      );
    },
    shopProductProviderMethods(): IShopProductProviderMethods {
      return {
        reloadData: this.reloadData
      };
    }
  },
  async created() {
    this.loadData();
  },
  beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.productScope,
      vm: this
    });
  },
  methods: {
    async loadData() {
      try {
        await this.getProductData(this.isReloading);
        this.pushViewItemEvent();
        this.hasRequestError = false;
      } catch (error) {
        this.$store.dispatch("api/handleError", error);
        this.hasRequestError = true;
      } finally {
        this.isLoading = false;
        this.isReloading = false;
      }
    },
    async reloadData() {
      this.isReloading = true;
      return this.loadData();
    },
    pushViewItemEvent() {
      try {
        if (!this.product) return;
        const {
          prices,
          id,
          name,
          category,
          unit_quantity,
          report_code_1,
          report_code_2
        } = this.product as IProduct;

        UPM_DATA_LAYER.push({ ecommerce: null });
        UPM_DATA_LAYER.push({
          event: "view_item",
          ecommerce: {
            currency: prices?.[0]?.currency_code,
            value: prices?.[0]?.price,
            items: [
              {
                item_id: id,
                item_name: name, // For reporting purposes we intentionally pass untranslated product name
                item_category: category?.name, // For reporting purposes we intentionally use untranslated category name
                price: prices?.[0]?.price,
                quantity: unit_quantity || 1,
                upm_report_code_1: report_code_1, // item-scoped custom Upmind parameter
                upm_report_code_2: report_code_2 // item-scoped custom Upmind parameter
              }
            ]
          }
        });
      } catch {
        // Silent catch – non critical
      }
    },
    getProductData(ignoreStored = false) {
      return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
        id: this.productId,
        scope: this.productScope,
        isBasketContext: true,
        clientApi: true,
        params: _.merge(
          {},
          { currency_id: this.currencyId },
          this.omitPromotions ? { omit_promotions: 1 } : {},
          this.coupons ? { promotions: this.coupons.join() } : {},
          this.basketId && this.basketId !== RegexMatch.NEW
            ? { basket_id: this.basketId }
            : {},
          this.basketProduct
            ? { basket_product_id: this.basketProduct.id }
            : {},
          this.isAdmin && this.accountId ? { account_id: this.accountId } : {}
        ),
        vm: this,
        ignoreStored
      });
    },
    shopProductProviderData(): IShopProductProviderData {
      return {
        product: this.product,
        isLoading: this.isLoading,
        isReloading: this.isReloading,
        hasRequestError: this.hasRequestError,
        isProductTrial: this.isProductTrial,
        hideProductPrices: this.hideProductPrices
      };
    }
  },
  render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $shopProductData: this.shopProductProviderData(),
        $shopProductMethods: this.shopProductProviderMethods
      })
    );
  }
});
</script>
